import type {Indexed} from '@iiiristram/ts-type-utils'
import {makeKey} from '@yandex-delivery/utils/makeKey'

import {NetworkOptions, TypedRequestConfig} from './types'
import {equals, flow, get, negate} from '@yandex-delivery/utils'

export function batch<TArgs extends unknown[], TRes>(
  fn: (...args: TArgs) => Promise<TRes>,
  getKey: (...args: TArgs) => string = makeKey,
) {
  const hash: Indexed<Promise<TRes> | undefined> = {}

  return function batchInner(...args: TArgs) {
    const key = getKey(...args)
    let promise = hash[key]
    if (promise) return promise

    function clearCache(res: TRes | Error) {
      hash[key] = undefined

      if (res instanceof Error) {
        throw res
      }

      return res
    }

    promise = fn(...args).then(clearCache, clearCache)

    hash[key] = promise
    return promise
  }
}

export function addHeaders<T extends TypedRequestConfig>(config: T, headers: Record<string, string>) {
  return {
    ...config,
    headers: {
      ...config.headers,
      ...headers,
    },
  }
}

const isNotDisabledHeadersMode = flow(get('meta.standardHeadersMode'), negate(equals('disable')))
export function addSafeHeaders<T extends TypedRequestConfig>(config: T, headers: Record<string, string>) {
  return {
    ...config,
    headers: {
      ...config.headers,
      // чтобы корректно послать CORS запрос config.meta?.standardHeadersMode должен быть disable
      ...(isNotDisabledHeadersMode(config) ? headers : {}),
    },
  }
}

const BLOB_MIME_TYPES = [
  'text/csv',
  'application/pdf',
  'application/octet-stream',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

export function isBlob(mime?: string): boolean {
  if (!mime) {
    return false
  }

  return BLOB_MIME_TYPES.some(type => {
    return mime.includes(type)
  })
}

export function getContentType(body: unknown) {
  // Для FormData браузер автоматически выставляет заголовок с нужными границами
  if (body instanceof FormData) {
    return undefined
  }

  // Для URLSearchParams задаём соответствующий тип
  if (body instanceof URLSearchParams) {
    return 'application/x-www-form-urlencoded'
  }

  // Если MIME-тип указан в blob.type, то не возвращаем Content-Type явно, чтобы браузер применил его автоматически
  if (body instanceof Blob) {
    if (body.type) {
      return undefined
    } else {
      // универсальный MIME-тип для произвольных двоичных данных
      return 'application/octet-stream'
    }
  }

  // Если body — обычный объект (перед отправкой его потребуется сериализовать в JSON), устанавливаем JSON Content-Type
  if (body !== null && typeof body === 'object') {
    return 'application/json'
  }

  // Для строк и прочих типов оставляем undefined
  return undefined
}

export function serializeFetchBody(data: unknown): BodyInit | null | undefined {
  if (data === null || data === undefined) {
    return data
  }

  if (
    data instanceof Blob ||
    data instanceof FormData ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data) || // для TypedArray, DataView и т.п.
    data instanceof ReadableStream
  ) {
    return data
  }

  if (typeof data === 'object') {
    return JSON.stringify(data)
  }

  return data as BodyInit
}

export function prepareFetchHeaders(headers: NetworkOptions['headers'], body: NetworkOptions['body']) {
  const fetchHeaders = headers
    ? Object.fromEntries(Object.entries(headers).map(([key, value]) => [key.toLowerCase(), value]))
    : {}

  if (!fetchHeaders['content-type']) {
    const contentType = getContentType(body)

    if (contentType) {
      fetchHeaders['content-type'] = contentType
    }
  }

  return fetchHeaders
}

const statusMessages: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  402: 'Payment Required',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  406: 'Not Acceptable',
  407: 'Proxy Authentication Required',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  411: 'Length Required',
  412: 'Precondition Failed',
  413: 'Content Too Large',
  414: 'URI Too Long',
  415: 'Unsupported Media Type',
  416: 'Range Not Satisfiable',
  417: 'Expectation Failed',
  418: "I'm a teapot",
  421: 'Misdirected Request',
  422: 'Unprocessable Content',
  423: 'Locked',
  424: 'Failed Dependency',
  426: 'Upgrade Required',
  428: 'Precondition Required',
  429: 'Too Many Requests',
  431: 'Request Header Fields Too Large',
  451: 'Unavailable For Legal Reasons',
  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
  505: 'HTTP Version Not Supported',
  506: 'Variant Also Negotiates',
  507: 'Insufficient Storage',
  508: 'Loop Detected',
  510: 'Not Extended',
  511: 'Network Authentication Required',
} as const

export function getFetchErrorMessage({url, status, body}: {url: string; status: number; body: unknown}) {
  const statusText = statusMessages[status] || 'Unknown Error'
  const urlMessage = url.split('?')[0] // отрезаем query параметры
  let bodyMessage: string | undefined

  if (typeof body === 'object' && body !== null) {
    // из тела ошибки берем только code и message, остальные поля игнорируем
    const code = 'code' in body ? body.code : undefined
    const message = 'message' in body ? body.message : undefined

    if (code || message) {
      bodyMessage = JSON.stringify({code, message})
    }
  }

  const baseMessage = `${status}: ${statusText}, url: "${urlMessage}"`

  return bodyMessage ? `${baseMessage}, body: ${bodyMessage}` : baseMessage
}
